import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * "סמן כטופל" — visible only when a gate has a declared fault.
 *
 * Phase 4 polish surface for the noise-filtered fault flow: the
 * silent-reset engine in services.fault_filter only flips
 * ``gate.has_fault`` after three S2 edges in 60 minutes, so when this
 * button is visible the fault is *real* and waiting for a human to
 * confirm it's handled. Clicking it closes the underlying
 * FaultIncident row, clears the red triangle on the map, and reads
 * any unread fault alert in one call.
 *
 * The component is shared between the customer's site-map panel and
 * the admin gate detail page; the only thing that differs is the
 * endpoint prefix, passed in via ``apiPrefix``.
 */
function FaultAcknowledgeButton({ gate, apiPrefix, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Local "we just acknowledged this" flag. The customer-side panel
  // reads ``gate`` from the parent's live-poll cache which only
  // refreshes every 30s — without this state the button would keep
  // showing until the next poll even though the server already
  // closed the incident. Latching here gives instantaneous UI
  // feedback regardless of what the parent does.
  const [justResolved, setJustResolved] = useState(false);

  // Re-arm the latch if the parent later hands us a *different* gate
  // (panel switching to another gate, or the same gate reopening a
  // new fault). Without this reset the button would stay hidden on
  // the next legitimate declaration.
  useEffect(() => {
    setJustResolved(false);
    setError(null);
  }, [gate?.id]);

  if (!gate || !gate.has_fault || justResolved) {
    return null;
  }

  const handleClick = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${apiPrefix}/gates/${gate.id}/fault/acknowledge`);
      setJustResolved(true);
      if (typeof onResolved === 'function') {
        onResolved();
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'לא הצליח לעדכן');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        style={{
          background: '#b91c1c',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '0.45rem 0.9rem',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer',
          alignSelf: 'flex-start',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'מעדכן…' : 'סמן תקלה כטופלה'}
      </button>
      {error && (
        <span style={{ color: '#b91c1c', fontSize: '0.75rem' }}>{error}</span>
      )}
    </div>
  );
}

export default FaultAcknowledgeButton;
